import React, { FC, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  makeSelectSelectedCompanyFields,
  selectVisibleRoutesBySelectedTaskFieldSeasonId,
} from '../../../Map/slice/selectors';
import { Input, List, Row } from 'antd';
import * as S from './SelectedFieldsSummary.style';
import { useMapSlice } from 'app/containers/Map/slice';
import { TextRegular12 } from 'styles/typography';
import { IFieldProgress, IFieldTask } from '../../slice/types';
import AreaTag from './AreaTag';
import { SearchOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

type FieldSummaryListItem = {
  id: number;
  name: string;
  progress?: number;
  areaNum: number;
  fieldSeasonId: number;
};

interface IProps {
  companyId?: number;
  fieldTasks?: IFieldTask[] | null;
  fieldTaskProgressUpdate?: IFieldProgress[] | null;
}

export const SelectedFieldsSummary: FC<IProps> = ({
  companyId,
  fieldTasks,
  fieldTaskProgressUpdate,
}) => {
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const { actions: mapActions } = useMapSlice();

  const [search, setSearch] = useState('');

  const selectedFieldsByCompanyId = useMemo(() => {
    return makeSelectSelectedCompanyFields(companyId);
  }, [companyId]);

  const selectedFields = useSelector(selectedFieldsByCompanyId);
  const selectedFieldSeasonId = useSelector(
    selectVisibleRoutesBySelectedTaskFieldSeasonId,
  );

  const fieldListItems: FieldSummaryListItem[] = selectedFields.map(field => {
    let progress = fieldTasks?.find(
      fieldTask => fieldTask.fieldSeason.id === field.fieldId,
    )?.progress;

    if (fieldTaskProgressUpdate) {
      progress = fieldTaskProgressUpdate?.find(
        fieldProgress => fieldProgress.fieldId === field.fieldId,
      )?.progress;
    }

    return {
      id: field.fieldId,
      fieldSeasonId: field.fieldId,
      name: field.name,
      progress,
      areaNum: field.areaNum,
    };
  });

  const fieldListItemsWithSearch = fieldListItems
    .filter(fieldItem => {
      const query = search.toLowerCase().trim();

      if (!query.length) {
        return true;
      }

      return Boolean(
        fieldItem.name && fieldItem.name.toLowerCase().includes(query),
      );
    })
    .sort((a, b) => a.name.localeCompare(b.name));

  return (
    <S.DetailsCard>
      <Input
        prefix={<SearchOutlined />}
        placeholder={t('Task.SearchField', 'Search field')}
        value={search}
        onChange={e => setSearch(e.target.value)}
        style={{ marginBottom: 14 }}
      />
      <List
        dataSource={fieldListItemsWithSearch}
        renderItem={({
          id,
          areaNum,
          name,
          progress,
          fieldSeasonId,
        }: FieldSummaryListItem) => (
          <List.Item
            onClick={() => {
              dispatch(mapActions.zoomOnPolygon(id));
              dispatch(
                mapActions.setVisibleRoutesBySelectedTaskFieldSeasonId(
                  selectedFieldSeasonId === fieldSeasonId
                    ? null
                    : fieldSeasonId,
                ),
              );
            }}
          >
            <Row
              wrap={false}
              justify="space-between"
              style={{ width: '100%' }}
              align="middle"
            >
              <TextRegular12 style={{ paddingRight: 10 }}>{name}</TextRegular12>
              <AreaTag areaNum={areaNum} progress={progress} />
            </Row>
          </List.Item>
        )}
      ></List>
    </S.DetailsCard>
  );
};
